import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Distributor } from '@wsphere/distributors/domain';
import { Policy, Provisioning, Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, map, Subject, takeUntil } from 'rxjs';
import { formatDurationFromDays } from '../../../policy/utils/duration.utils';
import { CheckoutSteps } from '../../pages';
import { ProvisioningSessionService } from '../../services';

type SummaryState = 'DESKTOP' | 'MOBILE';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-order-summary',
  templateUrl: './order-summary.component.html',
  standalone: false,
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  @ViewChild('confirmCancellation') confirmCancellationDialog?: DialogComponent;

  private sessionSvc = inject(ProvisioningSessionService);
  private destroy$ = new Subject<void>();

  @Output() closeDialog = new EventEmitter<void>();
  @Output() updatePreview = new EventEmitter<{ sessionId: string; data: Provisioning.UpdateProvisioningSessionDto }>();

  @Input() distributor: Distributor.Model | null = null;

  @Input() session: Provisioning.ProvisioningSession | null = null;

  @Input() loading: boolean | null = null;

  @PropertyListener('_preview') preview$ = new BehaviorSubject<Provisioning.WarrantyPreview | null>(null);
  @Input() _preview: Provisioning.WarrantyPreview | null = null;

  selectedTerm$ = this.preview$.pipe(
    map(preview => preview?.session.policy?.terms.find(term => term.id === preview.session.policyTermId) ?? null)
  );

  @Input() coverages: Warranty.WarrantyCoverage[] | null = null;

  @Input() addons: Provisioning.PreviewAddon[] | null = null;

  @Input() currentStep: CheckoutSteps | null = null;

  @Input() state: SummaryState = 'DESKTOP';

  @Input() staffView = false;

  protected finalStep = CheckoutSteps.Checkout;

  protected open = false;

  innerHeight = 0;

  markupDollars: number | null = null;

  /**
   * Whether the markup input is offered at checkout — controlled per-policy via `allowMarkup`.
   * Shown unless the policy explicitly disables it (undefined/legacy policies default to enabled).
   */
  get markupAllowed(): boolean {
    return Policy.isMarkupAllowed(this._preview?.session?.policy ?? this.session?.policy);
  }

  protected async removeAddon(
    selectedAddon: Policy.Plan.Addon,
    addons: Provisioning.PreviewAddon[],
    sessionId: string
  ) {
    const addonIds = addons.map(addon => addon.addon.id);
    const updatedList = addonIds.filter(id => id !== selectedAddon.id);
    const data: Provisioning.UpdateProvisioningSessionDto = {
      addonIds: updatedList,
    };
    this.updatePreview.emit({ sessionId, data });
  }

  cancel() {
    this.closeDialog.emit();
    this.sessionSvc.clearSession();
    this.sessionSvc.distributor$.next(null);
    this.sessionSvc.addons$.next(null);
    this.sessionSvc.preview$.next(null);
    // Also clear the initial-data subject — otherwise the next sale's Customer
    // step pre-fills the form with the prior customer's info (the session$ tap
    // falls back to data$ when session is null).
    this.sessionSvc.data$.next({});
    // FIXME: Provisioning session local storage
    // if (localStorage.getItem('provisioning-session')) localStorage.removeItem('provisioning-session');
  }

  resize() {
    this.innerHeight = (window.innerHeight / 6) * 5;
  }

  getTotalAmount(preview: Provisioning.WarrantyPreview) {
    let sub = preview.subtotal + (preview.markup ?? 0);
    preview.taxes.forEach(tax => (sub += tax.amount));
    // The billing total is the first payment, which alone carries the initiation fee and its tax.
    if (preview.initiationFee) {
      sub += preview.initiationFee.amount;
      preview.initiationFee.taxes.forEach(tax => (sub += tax.amount));
    }
    return sub;
  }

  onMarkupChange(sessionId: string) {
    const markupCents = this.markupDollars != null ? Math.round(this.markupDollars * 100) : null;
    this.updatePreview.emit({ sessionId, data: { markup: markupCents } });
  }

  formatTermDuration(duration?: number | null) {
    return formatDurationFromDays(duration ?? undefined);
  }

  // Dynamically resolved duration wins over the term's static configured duration.
  effectiveTermDuration(selectedTerm?: Policy.PolicyTerm | null) {
    return this._preview?.warranty?.termDuration ?? selectedTerm?.duration;
  }

  ngOnInit(): void {
    this.resize();
    this.preview$.pipe(takeUntil(this.destroy$)).subscribe(preview => {
      this.markupDollars = preview?.session?.markup != null ? preview.session.markup / 100 : null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
